class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    if (this.mainDetailsToggle.classList.contains('mega-menu--explore')) {
      this.closeExploreMenu();
      return;
    }

    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.sectionHeader = document.querySelector('.section-header');
    this.desktopNav = this.closest('.header__inline-menu');
    this.pointerDown = false;
    this.closeDelay = 150;
    this.panelOpenDuration = 560;
    this.panelCloseDuration = 380;
    this.contentCloseDuration = 180;
    this.switchOutDuration = 180;
    this.onResize = this.onResize.bind(this);

    this.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('mouseenter', this.open.bind(this));
    this.addEventListener('mouseleave', this.scheduleClose.bind(this));
    this.mainDetailsToggle.addEventListener('mouseenter', this.cancelClose.bind(this));
    this.mainDetailsToggle.addEventListener('mouseleave', this.scheduleClose.bind(this));
    this.content.addEventListener('mouseenter', this.cancelClose.bind(this));
    this.content.addEventListener('mouseleave', this.scheduleClose.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', this.onSummaryClick.bind(this));
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    if (HeaderMenu.activeMenu !== this) return;
    this.updateActiveMegaMenuHeight();
  }

  onPointerDown() {
    this.pointerDown = true;

    setTimeout(() => {
      this.pointerDown = false;
    }, 150);
  }

  onFocusIn() {
    if (this.pointerDown || this.suppressFocusOpen) return;
    this.open();
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.suppressFocusOpen = true;
    this.close();
    this.mainDetailsToggle.querySelector('summary').focus();
    requestAnimationFrame(() => {
      this.suppressFocusOpen = false;
    });
  }

  onSummaryClick(event) {
    if (!this.mainDetailsToggle.classList.contains('mega-menu--explore')) return;

    event.preventDefault();
    HeaderMenu.activeMenu === this ? this.close() : this.open();
  }

  cancelClose() {
    window.clearTimeout(this.closeTimeout);
    this.cancelSiblingCloseTimers();
  }

  scheduleClose() {
    this.cancelClose();

    this.closeTimeout = window.setTimeout(() => {
      if (this.matches(':hover') || this.mainDetailsToggle.matches(':hover') || this.content.matches(':hover')) return;
      this.close();
    }, this.closeDelay);
  }

  open() {
    this.cancelClose();

    if (this.resolvePendingSwitch()) return;

    if (HeaderMenu.activeMenu && HeaderMenu.activeMenu !== this) {
      const previousMenu = HeaderMenu.activeMenu;

      if (this.isAnimatedMegaMenu() && previousMenu.isAnimatedMegaMenu()) {
        this.switchFrom(previousMenu);
        return;
      }

      previousMenu.closeImmediately(true);
    }

    HeaderMenu.activeMenu = this;
    this.setHeaderActiveState(true);

    if (this.mainDetailsToggle.classList.contains('mega-menu--explore')) {
      this.openExploreMenu();
      return;
    }

    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
  }

  openExploreMenu() {
    if (this.mainDetailsToggle.open && this.mainDetailsToggle.classList.contains('is-opening')) return;

    this.clearAnimationTimers();
    this.mainDetailsToggle.classList.remove('is-closing', 'is-switching-out', 'is-switching-in');
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    this.sectionHeader?.classList.remove('dropdown-closing', 'dropdown-switching');
    this.sectionHeader?.classList.add('dropdown-opening');

    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.add('is-opening');
      this.updateActiveMegaMenuHeight();

      this.animationTimer = window.setTimeout(() => {
        this.sectionHeader?.classList.remove('dropdown-opening');
      }, this.motionDuration(this.panelOpenDuration));
    });
  }

  closeExploreMenu() {
    if (!this.mainDetailsToggle.open || this.mainDetailsToggle.classList.contains('is-closing')) return;

    this.clearAnimationTimers();
    this.mainDetailsToggle.classList.remove('is-opening');
    this.mainDetailsToggle.classList.add('is-closing');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    this.sectionHeader?.classList.remove('dropdown-opening', 'dropdown-switching');

    this.animationTimer = window.setTimeout(() => {
      if (!this.mainDetailsToggle.classList.contains('is-closing') || HeaderMenu.activeMenu) return;

      this.sectionHeader?.classList.add('dropdown-closing');
      this.sectionHeader?.style.setProperty('--active-mega-menu-height', '0px');

      this.panelTimer = window.setTimeout(() => {
        if (!this.mainDetailsToggle.classList.contains('is-closing') || HeaderMenu.activeMenu) return;

        this.finishDetailsClose();
        this.setHeaderActiveState(false);
      }, this.motionDuration(this.panelCloseDuration));
    }, this.motionDuration(this.contentCloseDuration));
  }

  close() {
    this.cancelClose();

    if (HeaderMenu.pendingSwitch?.source === this && HeaderMenu.activeMenu === HeaderMenu.pendingSwitch.target) return;

    if (HeaderMenu.pendingSwitch?.target === this) {
      const { source, target, timer } = HeaderMenu.pendingSwitch;
      window.clearTimeout(timer);
      HeaderMenu.pendingSwitch = null;
      target.finishDetailsClose();
      HeaderMenu.activeMenu = source;
      source.mainDetailsToggle.classList.remove('is-switching-out');
      source.mainDetailsToggle.classList.add('is-opening');
      source.close();
      return;
    }

    if (HeaderMenu.activeMenu === this) {
      HeaderMenu.activeMenu = null;
    }

    if (this.isAnimatedMegaMenu()) {
      this.closeExploreMenu();
      return;
    }

    super.close();
    this.setHeaderActiveState(Boolean(HeaderMenu.activeMenu));
  }

  closeImmediately(keepHeaderActive = false) {
    this.cancelClose();
    this.clearAnimationTimers();
    this.finishDetailsClose();

    if (HeaderMenu.activeMenu === this) {
      HeaderMenu.activeMenu = null;
    }

    this.setHeaderActiveState(keepHeaderActive || this.hasActiveHeaderMenu());
  }

  onToggle() {
    super.onToggle();

    if (this.mainDetailsToggle.classList.contains('mega-menu--explore') && !this.mainDetailsToggle.open) {
      this.mainDetailsToggle.classList.remove('is-opening', 'is-closing', 'is-switching-in', 'is-switching-out');
    }

    if (!this.header) return;
    this.setHeaderActiveState(Boolean(HeaderMenu.activeMenu), false);

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  cancelSiblingCloseTimers() {
    this.desktopNav?.querySelectorAll('header-menu').forEach((menu) => {
      if (menu === this) return;
      window.clearTimeout(menu.closeTimeout);
    });
  }

  hasActiveHeaderMenu() {
    return Array.from(this.desktopNav?.querySelectorAll('header-menu > details') || []).some(
      (details) => details.open || details.classList.contains('is-opening')
    );
  }

  updateActiveMegaMenuHeight() {
    if (!this.sectionHeader) return;

    if (!window.matchMedia('(min-width: 990px)').matches) {
      this.sectionHeader.style.removeProperty('--active-mega-menu-height');
      return;
    }

    if (!this.mainDetailsToggle.classList.contains('mega-menu--explore') || !this.mainDetailsToggle.open) return;

    const availableHeight = Math.max(0, window.innerHeight - this.content.getBoundingClientRect().top - 40);
    let contentHeight = this.content.scrollHeight;
    if (this.mainDetailsToggle.classList.contains('mega-menu--links-only')) {
      // Measure the links independently of the height inherited from the previous menu.
      const inner = this.content.querySelector('.mega-menu__inner');
      const styles = window.getComputedStyle(this.content);
      contentHeight = inner.offsetHeight + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    }
    const height = Math.ceil(Math.min(contentHeight, availableHeight));
    this.sectionHeader.style.setProperty('--active-mega-menu-height', `${height}px`);
  }

  setHeaderActiveState(isActive, updateHeight = true) {
    if (!this.header) return;

    this.header.preventHide = isActive;
    this.sectionHeader?.classList.toggle('dropdown-open', isActive);

    if (isActive && updateHeight) {
      this.updateActiveMegaMenuHeight();
    } else {
      if (!isActive) {
        this.sectionHeader?.classList.remove('dropdown-opening', 'dropdown-closing', 'dropdown-switching');
        this.sectionHeader?.style.removeProperty('--active-mega-menu-height');
      }
    }
  }

  isAnimatedMegaMenu() {
    return this.mainDetailsToggle.classList.contains('mega-menu--explore');
  }

  motionDuration(duration) {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : duration;
  }

  clearAnimationTimers() {
    window.clearTimeout(this.animationTimer);
    window.clearTimeout(this.panelTimer);
  }

  finishDetailsClose() {
    this.clearAnimationTimers();
    this.mainDetailsToggle.classList.remove('is-opening', 'is-closing', 'is-switching-in', 'is-switching-out');
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }

  switchFrom(previousMenu) {
    previousMenu.clearAnimationTimers();
    this.clearAnimationTimers();

    HeaderMenu.activeMenu = this;
    this.setHeaderActiveState(true, false);
    this.mainDetailsToggle.classList.remove('is-opening', 'is-closing', 'is-switching-in', 'is-switching-out');
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);

    previousMenu.mainDetailsToggle.classList.remove('is-opening', 'is-closing');
    previousMenu.mainDetailsToggle.classList.add('is-switching-out');
    previousMenu.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    this.sectionHeader?.classList.remove('dropdown-opening', 'dropdown-closing');
    this.sectionHeader?.classList.add('dropdown-switching');

    const timer = window.setTimeout(() => {
      if (HeaderMenu.pendingSwitch?.target !== this || HeaderMenu.activeMenu !== this) return;

      previousMenu.finishDetailsClose();
      HeaderMenu.pendingSwitch = null;
      this.mainDetailsToggle.classList.add('is-opening', 'is-switching-in');
      this.updateActiveMegaMenuHeight();
      this.scheduleSwitchCleanup();
    }, this.motionDuration(this.switchOutDuration));

    HeaderMenu.pendingSwitch = { source: previousMenu, target: this, timer };
  }

  resolvePendingSwitch() {
    const pendingSwitch = HeaderMenu.pendingSwitch;
    if (!pendingSwitch) return false;

    window.clearTimeout(pendingSwitch.timer);
    HeaderMenu.pendingSwitch = null;
    pendingSwitch.source.finishDetailsClose();

    if (pendingSwitch.target !== this) {
      pendingSwitch.target.finishDetailsClose();
      HeaderMenu.activeMenu = null;
      this.sectionHeader?.classList.remove('dropdown-switching');
      return false;
    }

    HeaderMenu.activeMenu = this;
    this.mainDetailsToggle.classList.add('is-opening', 'is-switching-in');
    this.updateActiveMegaMenuHeight();
    this.scheduleSwitchCleanup();
    return true;
  }

  scheduleSwitchCleanup() {
    this.animationTimer = window.setTimeout(() => {
      this.mainDetailsToggle.classList.remove('is-switching-in');
      this.sectionHeader?.classList.remove('dropdown-switching');
    }, this.motionDuration(500));
  }
}

HeaderMenu.activeMenu = null;
HeaderMenu.pendingSwitch = null;

customElements.define('header-menu', HeaderMenu);
